// DashboardRenderer.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getViewBlockDisplayTitle } from 'page/pageEditorLayoutUtils';

// Services
import { resolveDashboardLayoutDocument } from 'services/dashboards/DashboardLayoutModel';

// Hooks
import useDashboardLayoutPreviewData from './hooks/useDashboardLayoutPreviewData';

// CSS
import './DashboardRenderer.css';

// Components
import ViewRenderer from 'views/ViewRenderer';

const DEFAULT_LAYOUT_ROOT_KEY = 'layout_root';

function normalizeString(value) {
  return String(value || '').trim();
}

function ensureLayout(layout) {
  const nodes = { ...(layout?.nodes || {}) };
  const rootNodeKey = normalizeString(layout?.rootNodeKey || DEFAULT_LAYOUT_ROOT_KEY) || DEFAULT_LAYOUT_ROOT_KEY;

  if (!nodes[rootNodeKey]) {
    nodes[rootNodeKey] = {
      children: [],
      key: rootNodeKey,
      kind: 'stack',
    };
  }

  if (!Array.isArray(nodes[rootNodeKey]?.children)) {
    nodes[rootNodeKey].children = [];
  }

  return {
    nodes,
    rootNodeKey,
  };
}

/**
 * DashboardRenderer
 *
 * Read-only dashboard layout tree for base browse mode.
 *
 * @returns {ReactNode} The rendered dashboard body.
 */
const DashboardRenderer = ({
  childNodesByParent = {},
  dashboard = null,
  getType,
  onOpenView = null,
  userKey = null,
  views = [],
  baseNodes = [],
}) => {
  // ----- CONTEXT ----- //

  const language = useCurrentLanguage();
  const { theme } = useTheme();
  // ----- STATE ----- //

  const [selectedKeysByBlock, setSelectedKeysByBlock] = useState({});

  // ----- MEMOS ----- //

  const resolvedLayoutDocument = useMemo(() => {
    return resolveDashboardLayoutDocument(dashboard);
  }, [dashboard]);

  const layout = useMemo(() => {
    return ensureLayout(resolvedLayoutDocument.layout);
  }, [resolvedLayoutDocument.layout]);

  const blocksByKey = useMemo(() => {
    return resolvedLayoutDocument.blocksByKey || {};
  }, [resolvedLayoutDocument.blocksByKey]);

  // ----- HOOKS ----- //

  const previewData = useDashboardLayoutPreviewData({
    blocksByKey,
    childNodesByParent,
    getType,
    userKey,
    views,
    baseNodes,
  });

  // ----- HANDLERS ----- //

  const handleBlockTitleClick = useCallback((view) => {
    if (typeof onOpenView !== 'function' || !view) return;
    onOpenView(view);
  }, [onOpenView]);

  // ----- MEMOS ----- //

  const renderViewBlock = useCallback((blockKey, block) => {
    const cleanedBlockKey = normalizeString(blockKey);
    const preview = previewData.viewPreviewByBlockKey.get(cleanedBlockKey) || null;
    if (!preview?.view) return null;

    const blockTitle = getViewBlockDisplayTitle(block, preview.view);
    const typeKey = normalizeString(preview.view?.nodeType || preview.view?.typeKey);
    const selectedKeys = selectedKeysByBlock[cleanedBlockKey] || [];

    return (
      <div
        className="dashboard-renderer-view-block"
      >

        {/* VIEW TITLE */}
        {blockTitle ? (
          <div
            className={[
              'dashboard-renderer-view-title',
              typeof onOpenView === 'function' ? 'is-clickable' : '',
            ].filter(Boolean).join(' ')}
            style={{
              color: theme.foregroundMuted,
            }}
            role={typeof onOpenView === 'function' ? 'button' : undefined}
            tabIndex={typeof onOpenView === 'function' ? 0 : undefined}
            title={typeof onOpenView === 'function' ? 'Open view' : undefined}
            onClick={() => handleBlockTitleClick(preview.view)}
            onKeyDown={(event) => {
              if (typeof onOpenView !== 'function') return;
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              handleBlockTitleClick(preview.view);
            }}
          >
            {blockTitle}
          </div>
        ) : null}

        {/* VIEW CONTENT */}
        <div className="dashboard-renderer-view-content">
          <ViewRenderer
            addTypeKey={typeKey || null}
            displayMode="compact"
            language={language}
            nodes={preview.nodes}
            selectedKeys={selectedKeys}
            setSelectedKeys={(nextValue) => {
              setSelectedKeysByBlock((prev) => {
                const prevSelected = prev[cleanedBlockKey] || [];
                const nextSelected = typeof nextValue === 'function'
                  ? nextValue(prevSelected)
                  : nextValue;

                return {
                  ...prev,
                  [cleanedBlockKey]: Array.isArray(nextSelected) ? nextSelected : [],
                };
              });
            }}
            typeDef={preview.typeDef}
            view={preview.view}
          />
        </div>
      </div>
    );
  }, [
    handleBlockTitleClick,
    language,
    onOpenView,
    previewData.viewPreviewByBlockKey,
    selectedKeysByBlock,
    theme.foregroundMuted,
  ]);

  const renderBlock = useCallback((blockKey) => {
    const cleanedBlockKey = normalizeString(blockKey);
    if (!cleanedBlockKey) return null;

    const block = blocksByKey[cleanedBlockKey] || null;
    if (!block) return null;

    if (normalizeString(block?.type).toUpperCase() !== 'VIEW') {
      return null;
    }

    return renderViewBlock(cleanedBlockKey, block);
  }, [blocksByKey, renderViewBlock]);

  const renderLayoutNode = useCallback((nodeKey) => {
    const layoutNode = layout?.nodes?.[nodeKey] || null;
    if (!layoutNode) return null;

    if (layoutNode.kind === 'block_slot') {
      const blockContent = renderBlock(layoutNode.blockKey);
      if (!blockContent) return null;

      return (
        <div className="dashboard-renderer-slot" key={nodeKey}>
          {blockContent}
        </div>
      );
    }

    if (layoutNode.kind === 'row') {
      const children = Array.isArray(layoutNode.children) ? layoutNode.children : [];
      const renderedChildren = children
        .map((childKey) => renderLayoutNode(childKey))
        .filter(Boolean);

      if (!renderedChildren.length) return null;

      return (
        <div
          className="dashboard-renderer-row"
          style={{
            '--dashboard-renderer-columns': String(Math.max(renderedChildren.length, 1)),
          }}
          key={nodeKey}
        >
          {renderedChildren}
        </div>
      );
    }

    const children = Array.isArray(layoutNode.children) ? layoutNode.children : [];
    const renderedChildren = children
      .map((childKey) => renderLayoutNode(childKey))
      .filter(Boolean);

    if (!renderedChildren.length) return null;

    return (
      <div className="dashboard-renderer-stack" key={nodeKey}>
        {renderedChildren}
      </div>
    );
  }, [layout?.nodes, renderBlock]);

  const body = renderLayoutNode(layout.rootNodeKey);

  if (!body) {
    return (
      <div
        className="dashboard-renderer-empty"
      >
        This dashboard has no views yet. Use Edit Layout to add views.
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div className="dashboard-renderer-container">
      {body}
    </div>
  );
};

DashboardRenderer.displayName = 'DashboardRenderer';

export default React.memo(DashboardRenderer);
